import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Card, Typography, Flex, Select, DatePicker, Button, Table, Tag, message, Spin } from "antd";
import { FileExcelOutlined, ReloadOutlined, ClockCircleOutlined, CalendarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { getCookie } from "../../utils/cookieUtils";
import { getEmployeeCallReport } from "../../services/reportService";
import { getEmployees } from "../../services/employeeService";
import { getCompanySettings } from "../../services/organizationService";

const { Text } = Typography;
const { RangePicker } = DatePicker;

// Helper to convert seconds into "Xh Ym Zs" or "MM:SS" format
function formatSeconds(sec = 0) {
  if (!sec || sec <= 0) return "0s";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const parts = [];
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  if (s > 0 || parts.length === 0) parts.push(`${s}s`);
  return parts.join(" ");
}

function PeriodicReports() {
  const reduxCompanyCode = useSelector((state) => state.executor?.companyInfo?.companyCode);
  const user = useSelector((state) => state.session?.settings?.userInfo);
  const effectiveCompanyCode = reduxCompanyCode || user?.companyCode || getCookie("companyCode") || "";

  const [datePreset, setDatePreset] = useState("today");
  const [customRange, setCustomRange] = useState(null);
  const [selectedTag, setSelectedTag] = useState("all");
  const [callTypeFilter, setCallTypeFilter] = useState("all");
  const [durationFilter, setDurationFilter] = useState("all");
  const [callTimeFilter, setCallTimeFilter] = useState("all");

  const [loading, setLoading] = useState(false);
  const [companyTags, setCompanyTags] = useState([]);
  const [employeesList, setEmployeesList] = useState([]);
  const [reportRows, setReportRows] = useState([]);
  const [employeesMap, setEmployeesMap] = useState({});

  // Load live employees & DB tags to map employee names and tags
  const loadEmployees = useCallback(async () => {
    if (!effectiveCompanyCode) return;
    try {
      const [res, settingsRes] = await Promise.all([getEmployees(effectiveCompanyCode), getCompanySettings(effectiveCompanyCode)]);
      if (res?.success && Array.isArray(res.employees)) {
        setEmployeesList(res.employees);
        const map = {};
        res.employees.forEach((emp) => {
          map[emp._id] = emp;
          if (emp.mobile) map[emp.mobile] = emp;
        });
        setEmployeesMap(map);
      }
      if (settingsRes?.success && Array.isArray(settingsRes.settings?.tags)) {
        setCompanyTags(settingsRes.settings.tags);
      }
    } catch (e) {
      console.warn("Failed to load employees for report mapping", e);
    }
  }, [effectiveCompanyCode]);

  // Load live periodic report data
  const loadReportData = useCallback(async () => {
    if (!effectiveCompanyCode) return;
    setLoading(true);
    try {
      let fromStr, toStr;
      let period = datePreset;

      if (datePreset === "custom" && customRange?.[0] && customRange?.[1]) {
        fromStr = customRange[0].format("YYYY-MM-DD");
        toStr = customRange[1].format("YYYY-MM-DD");
      }

      const filters = {};
      if (callTypeFilter !== "all") filters.callType = callTypeFilter;
      if (durationFilter !== "all") filters.duration = durationFilter;
      if (callTimeFilter !== "all") filters.callTime = callTimeFilter;

      const empRes = await getEmployeeCallReport(effectiveCompanyCode, period, fromStr, toStr, filters);

      if (empRes.success && Array.isArray(empRes.employees)) {
        setReportRows(empRes.employees);
      } else {
        setReportRows([]);
      }
    } catch (err) {
      console.error("Failed to load live periodic reports:", err);
      message.error("Failed to load report data.");
    } finally {
      setLoading(false);
    }
  }, [effectiveCompanyCode, datePreset, customRange, callTypeFilter, durationFilter, callTimeFilter]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    loadReportData();
  }, [loadReportData]);

  // Derive dynamic available tags from company DB settings and employee registry
  const availableTags = useMemo(() => {
    const tagSet = new Set(Array.isArray(companyTags) ? companyTags : []);
    employeesList.forEach((emp) => {
      if (Array.isArray(emp.tags)) {
        emp.tags.forEach((t) => {
          if (t && String(t).trim()) tagSet.add(String(t).trim());
        });
      }
    });
    reportRows.forEach((r) => {
      if (Array.isArray(r.tags)) {
        r.tags.forEach((t) => {
          if (t && String(t).trim()) tagSet.add(String(t).trim());
        });
      }
    });
    return Array.from(tagSet);
  }, [companyTags, employeesList, reportRows]);

  // Process rows with employee registry data
  const processedData = useMemo(() => {
    return reportRows
      .map((row, idx) => {
        const emp = employeesMap[row.employeeId] || {};
        const empTags = Array.isArray(emp.tags) ? emp.tags : [];
        const primaryTag = empTags[0] || availableTags[0] || "—";

        return {
          key: row.employeeId || String(idx),
          empId: row.employeeId,
          name: emp.name || `Rep #${String(row.employeeId).slice(-4)}`,
          mobile: emp.mobile || "—",
          tag: primaryTag,
          tags: empTags,
          totalCalls: row.total || row.incoming + row.outgoing + row.missed + row.rejected || 0,
          connectedCalls: row.connected || 0,
          connectedDurationSec: row.totalDuration || 0,
          totalDurationSec: row.totalDuration || 0,
          incoming: row.incoming || 0,
          incomingDurationSec: row.incomingDuration || 0,
          incomingConnected: row.incomingConnected || 0,
          outgoing: row.outgoing || 0,
          outgoingDurationSec: row.outgoingDuration || 0,
          outgoingConnected: row.outgoingConnected || 0,
          missed: row.missed || 0,
          rejected: row.rejected || 0,
          notPicked: (row.missed || 0) + (row.rejected || 0),
          lastSyncTime: emp.lastSyncTime ? new Date(emp.lastSyncTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—",
        };
      })
      .filter((row) => selectedTag === "all" || row.tags.includes(selectedTag));
  }, [reportRows, employeesMap, selectedTag, availableTags]);

  // Handle Export to CSV
  const handleExportCSV = () => {
    if (processedData.length === 0) {
      message.warning("No data available to export.");
      return;
    }
    const headers = ["Employee Name", "Mobile", "Tag", "Total Calls", "Connected Calls", "Connected Duration", "Incoming Calls", "Incoming Duration", "Outgoing Calls", "Outgoing Duration", "Missed / Rejected", "Last Synced"];

    const csvContent = [headers.join(","), ...processedData.map((r) => [`"${r.name}"`, `"${r.mobile}"`, `"${r.tag}"`, r.totalCalls, r.connectedCalls, `"${formatSeconds(r.connectedDurationSec)}"`, r.incoming, `"${formatSeconds(r.incomingDurationSec)}"`, r.outgoing, `"${formatSeconds(r.outgoingDurationSec)}"`, r.notPicked, `"${r.lastSyncTime}"`].join(","))].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `DealVoice_Periodic_Report_${datePreset}_${dayjs().format("YYYYMMDD_HHmm")}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    message.success("Periodic report exported successfully!");
  };

  // Ant Design Columns Definition
  const columns = [
    {
      title: "Employee",
      dataIndex: "name",
      key: "name",
      fixed: "left",
      width: 180,
      render: (text, record) => (
        <Flex vertical gap={2}>
          <Text strong style={{ color: "#0f172a" }}>
            {text}
          </Text>
          <Text type="secondary" style={{ fontSize: "0.75rem" }}>
            {record.mobile}
          </Text>
        </Flex>
      ),
    },
    {
      title: "Tag",
      dataIndex: "tag",
      key: "tag",
      width: 130,
      render: (tag) => {
        let color = "blue";
        if (tag === "Enterprise") color = "purple";
        if (tag === "Support") color = "orange";
        if (tag === "Field Rep") color = "cyan";
        return (
          <Tag color={color} style={{ borderRadius: 6, fontWeight: 500 }}>
            {tag}
          </Tag>
        );
      },
    },
    {
      title: "Total Calls",
      dataIndex: "totalCalls",
      key: "totalCalls",
      align: "center",
      width: 130,
      sorter: (a, b) => a.totalCalls - b.totalCalls,
      render: (val) => (
        <Text strong style={{ color: "#2459d3" }}>
          {val}
        </Text>
      ),
    },
    {
      title: "Connected",
      dataIndex: "connectedCalls",
      key: "connectedCalls",
      align: "center",
      width: 130,
      sorter: (a, b) => a.connectedCalls - b.connectedCalls,
      render: (val) => (
        <Tag color="success" style={{ borderRadius: 6, fontWeight: 600 }}>
          {val}
        </Tag>
      ),
    },
    {
      title: "Connected Duration",
      dataIndex: "connectedDurationSec",
      key: "connectedDurationSec",
      width: 180,
      sorter: (a, b) => a.connectedDurationSec - b.connectedDurationSec,
      render: (sec) => (
        <Flex align="center" gap={6}>
          <ClockCircleOutlined style={{ color: "#10b981", fontSize: "0.85rem" }} />
          <Text strong style={{ color: "#0f172a" }}>
            {formatSeconds(sec)}
          </Text>
        </Flex>
      ),
    },
    {
      title: "Incoming",
      key: "incoming",
      align: "center",
      children: [
        {
          title: "Calls",
          dataIndex: "incoming",
          key: "incomingCalls",
          align: "center",
          width: 90,
          render: (val) => <Text>{val}</Text>,
        },
        {
          title: "Duration",
          dataIndex: "incomingDurationSec",
          key: "incomingDurationSec",
          align: "center",
          width: 120,
          render: (sec) => <Text type="secondary">{formatSeconds(sec)}</Text>,
        },
      ],
    },
    {
      title: "Outgoing",
      key: "outgoing",
      align: "center",
      children: [
        {
          title: "Calls",
          dataIndex: "outgoing",
          key: "outgoingCalls",
          align: "center",
          width: 90,
          render: (val) => <Text>{val}</Text>,
        },
        {
          title: "Duration",
          dataIndex: "outgoingDurationSec",
          key: "outgoingDurationSec",
          align: "center",
          width: 120,
          render: (sec) => <Text type="secondary">{formatSeconds(sec)}</Text>,
        },
      ],
    },
    {
      title: "Unanswered",
      dataIndex: "notPicked",
      key: "notPicked",
      align: "center",
      width: 120,
      render: (val) => (
        <Tag color="error" style={{ borderRadius: 6 }}>
          {val}
        </Tag>
      ),
    },
    {
      title: "Last Sync",
      dataIndex: "lastSyncTime",
      key: "lastSyncTime",
      width: 160,
      render: (time) => (
        <Text type="secondary" style={{ fontSize: "0.8rem" }}>
          {time}
        </Text>
      ),
    },
  ];

  return (
    <Spin spinning={loading} tip="Loading reports...">
      <Flex vertical gap={24} style={{ paddingBottom: 40 }}>
        {/* ── HEADER FILTER STRIP ── */}
        <Card
          bordered={false}
          style={{
            borderRadius: 14,
            background: "#ffffff",
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
          }}
        >
          <Flex justify="space-between" align="center" wrap="wrap" gap={16}>
            {/* Left Controls: Date Range & Preset Filters */}
            <Flex align="center" gap={12} wrap="wrap">
              <Flex align="center" gap={6}>
                <CalendarOutlined style={{ color: "#2459d3" }} />
                <Text strong style={{ fontSize: "0.85rem" }}>
                  Date Period:
                </Text>
              </Flex>

              <Select
                value={datePreset}
                onChange={(val) => {
                  setDatePreset(val);
                  if (val !== "custom") setCustomRange(null);
                }}
                style={{ width: 140 }}
                options={[
                  { value: "today", label: "Today" },
                  { value: "yesterday", label: "Yesterday" },
                  { value: "lastweek", label: "Last 7 Days" },
                  { value: "custom", label: "Custom Range" },
                ]}
              />

              {datePreset === "custom" && <RangePicker value={customRange} onChange={(dates) => setCustomRange(dates)} style={{ width: 250 }} allowClear={false} />}

              <Select value={selectedTag} onChange={setSelectedTag} style={{ width: 140 }} options={[{ value: "all", label: "All Tags" }, ...availableTags.map((t) => ({ value: t, label: t }))]} />

              <Select
                value={callTypeFilter}
                onChange={setCallTypeFilter}
                style={{ width: 130 }}
                options={[
                  { value: "all", label: "All Types" },
                  { value: "incoming", label: "Incoming" },
                  { value: "outgoing", label: "Outgoing" },
                  { value: "missed", label: "Missed" },
                  { value: "rejected", label: "Rejected" },
                ]}
              />

              <Select
                value={durationFilter}
                onChange={setDurationFilter}
                style={{ width: 120 }}
                options={[
                  { value: "all", label: "Any Duration" },
                  { value: "< 1 min", label: "< 1 min" },
                  { value: "1-5 min", label: "1-5 min" },
                  { value: "> 5 min", label: "> 5 min" },
                ]}
              />

              <Select
                value={callTimeFilter}
                onChange={setCallTimeFilter}
                style={{ width: 130 }}
                options={[
                  { value: "all", label: "Any Time" },
                  { value: "Morning", label: "Morning" },
                  { value: "Afternoon", label: "Afternoon" },
                  { value: "Evening", label: "Evening" },
                  { value: "Night", label: "Night" },
                ]}
              />
            </Flex>

            {/* Right Controls: Refresh & Export Button */}
            <Flex align="center" gap={12}>
              <Button icon={<ReloadOutlined spin={loading} />} onClick={loadReportData} style={{ borderRadius: 8 }}>
                Refresh
              </Button>
              <Button
                type="primary"
                icon={<FileExcelOutlined />}
                onClick={handleExportCSV}
                style={{
                  borderRadius: 8,
                  background: "#10b981",
                  borderColor: "#10b981",
                }}
              >
                Export CSV Report
              </Button>
            </Flex>
          </Flex>
        </Card>

        {/* ── DETAILED EMPLOYEE STATISTICS TABLE ── */}
        <Card
          bordered={false}
          title={
            <Flex justify="space-between" align="center">
              <Flex vertical gap={2}>
                <Text strong style={{ fontSize: "1.05rem" }}>
                  Employee Call Statistics & Telemetry Breakdown
                </Text>
                <Text type="secondary" style={{ fontSize: "0.75rem" }}>
                  Showing {processedData.length} sales reps with synced call records
                </Text>
              </Flex>
            </Flex>
          }
          style={{
            borderRadius: 14,
            boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
            background: "#ffffff",
          }}
        >
          <Table columns={columns} dataSource={processedData} pagination={{ pageSize: 10, showSizeChanger: true }} scroll={{ x: 1350 }} size="middle" className="qa-antd-table" />
        </Card>
      </Flex>
    </Spin>
  );
}

export default PeriodicReports;
